import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { ButtonV2, Input, Modal } from '@asigna/kit';
import cx from 'classnames';
import { AddCustomAppDialog, Dropdown, NoData } from 'components';
import { useActivateSafeFromUrlParam, useReadOnlyWarning } from 'hooks';
import { isEmpty } from 'lodash';
import { useAppsIframe } from 'store';

import { Iframe } from './components/Iframe';
import appsIcon from './util/images/apps.svg';
import { APP_CATEGORIES, APP_LIST, AppCategory, Application, AppsMetadata } from './appList';
import { AddAppButton, AppCard, CustomAppCard } from './components';
import { WHITELIST_APPLY } from './constants';

type FiilterOptions = 'All apps' | AppCategory;

const filterMenuOptions: FiilterOptions[] = ['All apps', ...APP_CATEGORIES];
const tabs = ['All apps', 'Custom apps'];

export const AppsPage: React.FC = () => {
  useActivateSafeFromUrlParam();
  const [selectedTab, setSelectedTab] = useState<string>('All apps');
  const [isAddAppModalShow, setAddAppModalShow] = useState(false);
  const [customApps, setCustomApps] = useState<AppsMetadata[]>([]);
  const [filterMenuItem, setFilterMenuItem] = useState<FiilterOptions>('All apps');
  const [appSearchInput, setAppSearchInput] = useState('');
  const readOnlyWarning = useReadOnlyWarning();

  const { isIframeRun, setIframeRun, setSidebarHidden, selectedApp, setSelectedApp } =
    useAppsIframe();

  useEffect(() => {
    const savedApps = localStorage.getItem('customApps');
    setCustomApps(savedApps ? JSON.parse(savedApps) : []);

    return () => {
      setIframeRun(false);
    };
  }, []);

  const apps = useMemo(() => {
    return APP_LIST.filter(
      (item) =>
        (filterMenuItem === 'All apps' || item.category.includes(filterMenuItem)) &&
        item.caption.toUpperCase().indexOf(appSearchInput.toUpperCase()) > -1
    );
  }, [filterMenuItem, appSearchInput]);

  const refreshApps = () => {
    const savedApps = localStorage.getItem('customApps');
    setCustomApps(savedApps ? JSON.parse(savedApps) : []);
  };

  const handleAppClick = (app: Application) => {
    if (app.isExternal) {
      window.open(app.url, '_blank');
    } else {
      setSelectedApp(app.url);
      setIframeRun(true);
      setSidebarHidden(true);
    }
  };

  const handleCustomAppClick = (e: SyntheticEvent, url: string) => {
    e.stopPropagation();
    setSelectedApp(url);
    setIframeRun(true);
    setSidebarHidden(true);
  };

  const handleDeleteAppClick = (e: SyntheticEvent, url: string) => {
    e.stopPropagation();
    const updatedApps = customApps.filter((app) => app.url !== url);
    setCustomApps(updatedApps);
    localStorage.setItem('customApps', JSON.stringify(updatedApps));
  };

  return (
    <div
      className={
        isIframeRun ? 'flex w-full h-[100vh]' : 'flex w-full pr-0 sm:pr-24 pl-0 h-screen py-16'
      }
    >
      <div
        className={
          isIframeRun
            ? 'flex flex-col w-full p-0 overflow-y-scroll h-[100vh]'
            : 'flex flex-col rounded-[16px] sm:bg-black-1 w-full relative px-20 sm:px-32 py-8 sm:py-20 overflow-y-scroll'
        }
      >
        {!isIframeRun && (
          <>
            <div className="flex flex-col lg:flex-row items-start justify-between w-full">
              <div className="flex flex-col">
                <h1 className="text-r24 mb-8 sm:mb-20">Explore the Stacks Apps ecosystem</h1>
                <div className="flex flex-wrap gap-4 mb-16 lg:mb-24">
                  <p className="text-r14 text-regular">
                    Are you an app developer? You can{` `}
                    <a
                      href={WHITELIST_APPLY}
                      target="_blank"
                      className="text-primary"
                      rel="noreferrer"
                    >
                      apply here
                    </a>
                    {` `}
                    to be added to our whitelisted applications.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-8 mb-24 lg:mb-0 w-full lg:w-auto">
                <Dropdown
                  item={filterMenuItem}
                  items={filterMenuOptions}
                  onChange={(val) => setFilterMenuItem(val as AppCategory)}
                />
                <Input
                  value={appSearchInput}
                  placeholder="Search..."
                  type="text"
                  onChange={(e) => {
                    setAppSearchInput(e.target.value);
                  }}
                  className="bg-black-2"
                />
              </div>
            </div>
            <div className="flex gap-8 mb-24">
              {tabs.map((tab) => {
                return (
                  <button
                    onClick={() => setSelectedTab(tab)}
                    className={cx(
                      'px-20 py-4 border rounded-[40px] h-36 text-r13',
                      tab === selectedTab
                        ? 'bg-primary-10 border-primary text-white hover:bg-primary-20'
                        : 'bg-black-2 border-white-10 text-regular hover:bg-white-10'
                    )}
                    key={tab}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </>
        )}
        {isIframeRun || Boolean(selectedApp) ? (
          <Iframe url={selectedApp} />
        ) : selectedTab === 'All apps' ? (
          <div className="flex w-full mb-36 flex-col gap-16">
            {isEmpty(apps) ? (
              <div className="mt-40 mx-32">
                <NoData />
              </div>
            ) : apps.length > 0 ? (
              <div className="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 flex-wrap gap-16 sm:gap-24 2xl:gap-32">
                {apps.map((item) => (
                  <AppCard key={item.url} app={item} onClick={handleAppClick} />
                ))}
              </div>
            ) : (
              <div className="w-full"></div>
            )}
          </div>
        ) : (
          <>
            {customApps.length > 0 ? (
              <div className="w-full my-36">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-32">
                  <AddAppButton onClick={() => setAddAppModalShow(true)} />
                  {customApps.map((item) => (
                    <CustomAppCard
                      key={item.url}
                      app={item}
                      onClick={(e: SyntheticEvent) => handleCustomAppClick(e, item.url || '')}
                      onDelete={(e: SyntheticEvent) => handleDeleteAppClick(e, item.url || '')}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col mt-[120px] items-center gap-8">
                <div className="w-64 h-64 rounded-[50%] flex items-center justify-center border border-[.5px] border-black-4 bg-black-2">
                  <img src={appsIcon} width={24} height={24} alt="apps" />
                </div>
                <p className="text-r16 text-regular text-center mb-16">
                  You don’t have any custom apps.
                </p>
                <ButtonV2
                  message="Add Custom App"
                  onClick={() => setAddAppModalShow(true)}
                  disabled={Boolean(readOnlyWarning)}
                  disableHint={readOnlyWarning}
                />
              </div>
            )}
            <Modal onClose={() => setAddAppModalShow(false)} isShow={isAddAppModalShow}>
              <AddCustomAppDialog
                onClose={() => {
                  refreshApps();
                  setAddAppModalShow(false);
                }}
              />
            </Modal>
          </>
        )}
      </div>
    </div>
  );
};
